// Secure Storage Utility
// Provides encrypted storage with automatic expiration for sensitive data

import { secureLogger } from './secureLogger';

// Simple encryption using base64 and XOR (for client-side, this provides basic obfuscation)
// Note: For production, consider using Web Crypto API for stronger encryption
const STORAGE_KEY_PREFIX = 'secure_';
const ENCRYPTION_KEY = 'ab-drive-security-key-2024'; // In production, this should be environment-specific

/**
 * Simple XOR encryption for client-side data obfuscation
 * @param {string} text - Text to encrypt/decrypt
 * @param {string} key - Encryption key
 * @returns {string} - Encrypted/decrypted text
 */
const xorEncrypt = (text, key) => {
  let result = '';
  for (let i = 0; i < text.length; i++) {
    result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return btoa(result); // Base64 encode
};

/**
 * XOR decryption
 * @param {string} encryptedText - Encrypted text to decrypt
 * @param {string} key - Decryption key
 * @returns {string} - Decrypted text
 */
const xorDecrypt = (encryptedText, key) => {
  try {
    const decoded = atob(encryptedText); // Base64 decode
    let result = '';
    for (let i = 0; i < decoded.length; i++) {
      result += String.fromCharCode(decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return result;
  } catch (error) {
    secureLogger.error('Failed to decrypt data:', error);
    return null;
  }
};

/**
 * Creates a secure storage item with encryption and expiration
 * @param {any} data - Data to store
 * @param {number} expirationMinutes - Minutes until expiration (default: 24 hours)
 * @returns {string} - Encrypted storage string
 */
const createSecureItem = (data, expirationMinutes = 1440) => {
  const item = {
    data,
    timestamp: Date.now(),
    expiration: Date.now() + (expirationMinutes * 60 * 1000)
  };
  
  const jsonString = JSON.stringify(item);
  return xorEncrypt(jsonString, ENCRYPTION_KEY);
};

/**
 * Retrieves and validates a secure storage item
 * @param {string} encryptedItem - Encrypted storage item
 * @returns {any|null} - Decrypted data or null if expired/invalid
 */
const getSecureItem = (encryptedItem) => {
  if (!encryptedItem) return null;
  
  try {
    const decrypted = xorDecrypt(encryptedItem, ENCRYPTION_KEY);
    if (!decrypted) return null;
    
    const item = JSON.parse(decrypted);
    
    // Check expiration
    if (Date.now() > item.expiration) {
      secureLogger.warn('Secure storage item expired');
      return null;
    }
    
    return item.data;
  } catch (error) {
    secureLogger.error('Failed to retrieve secure item:', error);
    return null;
  }
};

/**
 * Secure storage interface
 */
export const secureStorage = {
  /**
   * Store data securely with encryption and expiration
   * @param {string} key - Storage key
   * @param {any} data - Data to store
   * @param {number} expirationMinutes - Minutes until expiration
   */
  setItem: (key, data, expirationMinutes = 1440) => {
    try {
      const secureItem = createSecureItem(data, expirationMinutes);
      localStorage.setItem(STORAGE_KEY_PREFIX + key, secureItem);
      secureLogger.info(`Secure item stored: ${key}`);
    } catch (error) {
      secureLogger.error(`Failed to store secure item: ${key}`, error);
    }
  },

  /**
   * Retrieve data securely with automatic expiration check
   * @param {string} key - Storage key
   * @returns {any|null} - Retrieved data or null if expired/invalid
   */
  getItem: (key) => {
    try {
      const encryptedItem = localStorage.getItem(STORAGE_KEY_PREFIX + key);
      const data = getSecureItem(encryptedItem);
      
      if (data === null) {
        // Clean up expired/invalid items
        secureStorage.removeItem(key);
      }
      
      return data;
    } catch (error) {
      secureLogger.error(`Failed to retrieve secure item: ${key}`, error);
      return null;
    }
  },

  /**
   * Remove secure storage item
   * @param {string} key - Storage key
   */
  removeItem: (key) => {
    try {
      localStorage.removeItem(STORAGE_KEY_PREFIX + key);
      secureLogger.info(`Secure item removed: ${key}`);
    } catch (error) {
      secureLogger.error(`Failed to remove secure item: ${key}`, error);
    }
  },

  /**
   * Check if a secure item exists and is valid
   * @param {string} key - Storage key
   * @returns {boolean} - True if item exists and is valid
   */
  hasItem: (key) => {
    const data = secureStorage.getItem(key);
    return data !== null;
  },

  /**
   * Clear all secure storage items
   */
  clear: () => {
    try {
      const keys = Object.keys(localStorage).filter(key => key.startsWith(STORAGE_KEY_PREFIX));
      keys.forEach(key => localStorage.removeItem(key));
      secureLogger.info('All secure storage items cleared');
    } catch (error) {
      secureLogger.error('Failed to clear secure storage', error);
    }
  },

  /**
   * Get expiration time for a stored item
   * @param {string} key - Storage key
   * @returns {Date|null} - Expiration date or null if item doesn't exist
   */
  getExpiration: (key) => {
    try {
      const encryptedItem = localStorage.getItem(STORAGE_KEY_PREFIX + key);
      if (!encryptedItem) return null;
      
      const decrypted = xorDecrypt(encryptedItem, ENCRYPTION_KEY);
      if (!decrypted) return null;
      
      const item = JSON.parse(decrypted);
      return new Date(item.expiration);
    } catch (error) {
      secureLogger.error(`Failed to get expiration for: ${key}`, error);
      return null;
    }
  }
};

/**
 * Token-specific storage functions with appropriate expiration times
 */
export const tokenStorage = {
  setToken: (token, expirationMinutes = 1440) => { // 24 hours default - matches backend JWT expiration
    secureStorage.setItem('auth_token', token, expirationMinutes);
  },

  getToken: () => {
    return secureStorage.getItem('auth_token');
  },

  removeToken: () => {
    secureStorage.removeItem('auth_token');
  },

  hasValidToken: () => {
    return secureStorage.hasItem('auth_token');
  },

  getTokenExpiration: () => {
    return secureStorage.getExpiration('auth_token');
  }
};

/**
 * Session monitoring for security
 */
export const sessionMonitor = {
  /**
   * Check if session is about to expire (within 30 minutes)
   * @returns {boolean} - True if session expires soon
   */
  isSessionExpiringSoon: () => {
    const expiration = tokenStorage.getTokenExpiration();
    if (!expiration) return false;
    
    const thirtyMinutesFromNow = Date.now() + (30 * 60 * 1000);
    return expiration.getTime() < thirtyMinutesFromNow;
  },

  /**
   * Get remaining session time in minutes
   * @returns {number|null} - Minutes remaining or null if no session
   */
  getRemainingSessionTime: () => {
    const expiration = tokenStorage.getTokenExpiration();
    if (!expiration) return null;
    
    const remaining = expiration.getTime() - Date.now();
    return Math.max(0, Math.floor(remaining / (60 * 1000)));
  },

  /**
   * Check if user has been inactive for a specific duration
   * @param {number} minutes - Minutes of inactivity to check
   * @returns {boolean} - True if user has been inactive for the specified time
   */
  isUserInactive: (minutes = 10) => {
    const lastActivity = localStorage.getItem('lastUserActivity');
    if (!lastActivity) return true;
    
    const inactiveTime = Date.now() - parseInt(lastActivity);
    return inactiveTime > (minutes * 60 * 1000);
  }
};

export default secureStorage; 